import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import {
  getDoctorAppointments,
  submitConsultation,
} from '../services/consultationApi';
import { getMyArticles, formatDuration } from '../services/articlesApi';
import RecentActivityCard from '../components/RecentActivityCard';

/* ─── Status meta ───────────────────────────────────────────────── */
const STATUS_META = {
  CONFIRMED: { label: 'Confirmed', color: '#059669', bg: '#ecfdf5', border: '#6ee7b7' },
  PENDING:   { label: 'Pending',   color: '#d97706', bg: '#fffbeb', border: '#fde68a' },
  COMPLETED: { label: 'Completed', color: '#6366f1', bg: '#eef2ff', border: '#c7d2fe' },
  CANCELLED: { label: 'Cancelled', color: '#dc2626', bg: '#fef2f2', border: '#fca5a5' },
  NO_SHOW:   { label: 'No Show',   color: '#92400e', bg: '#fef3c7', border: '#fde68a' },
};

const TYPE_META = {
  ARTICLE:    { label: 'Article',     icon: '📄', color: '#4f46e5', bg: '#eef2ff',  border: '#c7d2fe' },
  VIDEO:      { label: 'Video',       icon: '🎬', color: '#dc2626', bg: '#fef2f2',  border: '#fca5a5' },
  WHITEPAPER: { label: 'White Paper', icon: '📋', color: '#0891b2', bg: '#ecfeff',  border: '#a5f3fc' },
};

const CONTENT_FILTERS = [
  { value: 'ALL',        label: 'All' },
  { value: 'ARTICLE',    label: 'Articles' },
  { value: 'VIDEO',      label: 'Videos' },
  { value: 'WHITEPAPER', label: 'White Papers' },
];

/* ─── Small helpers ─────────────────────────────────────────────── */
const StatusBadge = ({ status }) => {
  const m = STATUS_META[status] || STATUS_META['CONFIRMED'];
  return (
    <span style={{
      fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 9999,
      background: m.bg, color: m.color, border: `1px solid ${m.border}`,
      fontFamily: 'DM Sans, sans-serif', whiteSpace: 'nowrap',
    }}>
      {m.label}
    </span>
  );
};

const SkeletonCard = () => (
  <div style={{ background: '#fff', borderRadius: 16, padding: '18px 20px', border: '1px solid #f3f4f6' }}>
    <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
      <div className="skeleton" style={{ width: 44, height: 44, borderRadius: '50%', flexShrink: 0 }} />
      <div style={{ flex: 1 }}>
        <div className="skeleton skeleton-line" style={{ width: '45%', height: 13, marginBottom: 8 }} />
        <div className="skeleton skeleton-line" style={{ width: '65%', height: 11, marginBottom: 6 }} />
        <div className="skeleton skeleton-line" style={{ width: '35%', height: 11 }} />
      </div>
      <div className="skeleton" style={{ width: 80, height: 22, borderRadius: 9999 }} />
    </div>
  </div>
);

/* ─── Attendance Popup ──────────────────────────────────────────── */
const AttendancePopup = ({ appt, onClose, onAttended, onNotAttended, submitting }) => (
  <div
    style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(15,10,40,0.55)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '20px 16px', animation: 'fadeUp .18s ease both',
    }}
    onClick={e => { if (e.target === e.currentTarget && !submitting) onClose(); }}
  >
    <div style={{
      background: '#fff', borderRadius: 20, width: '100%', maxWidth: 400,
      boxShadow: '0 8px 40px rgba(79,70,229,0.2)',
      animation: 'dropIn .2s ease both', overflow: 'hidden',
    }}>
      <div style={{
        padding: '20px 24px 16px',
        borderBottom: '1px solid #f3f4f6',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
      }}>
        <div>
          <div style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700, fontSize: 16, color: '#1e1b4b', marginBottom: 4 }}>
            📋 Patient Attendance
          </div>
          <div style={{ fontSize: 12, color: '#9ca3af', fontFamily: 'DM Sans, sans-serif' }}>
            {appt.patientName || 'Patient'}
            {appt.appointmentDate
              ? ` · ${new Date(appt.appointmentDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`
              : ''}
            {appt.slotTime ? ` · 🕐 ${appt.slotTime}` : ''}
          </div>
        </div>
        <button
          onClick={onClose}
          disabled={submitting}
          style={{
            width: 32, height: 32, borderRadius: '50%', background: '#f3f4f6',
            border: 'none', cursor: 'pointer', fontSize: 18, color: '#6b7280',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}
        >×</button>
      </div>
      <div style={{ padding: '24px' }}>
        <p style={{
          fontSize: 13.5, color: '#374151', fontFamily: 'DM Sans, sans-serif',
          marginBottom: 20, lineHeight: 1.6, textAlign: 'center',
        }}>
          Did the patient attend this appointment?
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <button
            onClick={onAttended}
            disabled={submitting}
            style={{
              width: '100%', padding: '14px 20px', borderRadius: 14,
              background: 'linear-gradient(135deg, #ecfdf5, #d1fae5)',
              border: '2px solid #6ee7b7', color: '#059669',
              fontSize: 15, fontWeight: 700, cursor: submitting ? 'not-allowed' : 'pointer',
              fontFamily: 'DM Sans, sans-serif', transition: 'all .15s',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
            }}
            onMouseEnter={e => { if (!submitting) { e.currentTarget.style.background = '#059669'; e.currentTarget.style.color = '#fff'; e.currentTarget.style.borderColor = '#059669'; } }}
            onMouseLeave={e => { e.currentTarget.style.background = 'linear-gradient(135deg, #ecfdf5, #d1fae5)'; e.currentTarget.style.color = '#059669'; e.currentTarget.style.borderColor = '#6ee7b7'; }}
          >
            ✅ Attended
            <span style={{ fontSize: 12, fontWeight: 500, opacity: 0.75 }}>→ Fill consultation form</span>
          </button>
          <button
            onClick={onNotAttended}
            disabled={submitting}
            style={{
              width: '100%', padding: '14px 20px', borderRadius: 14,
              background: submitting ? '#f3f4f6' : 'linear-gradient(135deg, #fef2f2, #fee2e2)',
              border: '2px solid #fca5a5', color: '#dc2626',
              fontSize: 15, fontWeight: 700, cursor: submitting ? 'not-allowed' : 'pointer',
              fontFamily: 'DM Sans, sans-serif', transition: 'all .15s',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
            }}
            onMouseEnter={e => { if (!submitting) { e.currentTarget.style.background = '#dc2626'; e.currentTarget.style.color = '#fff'; e.currentTarget.style.borderColor = '#dc2626'; } }}
            onMouseLeave={e => { if (!submitting) { e.currentTarget.style.background = 'linear-gradient(135deg, #fef2f2, #fee2e2)'; e.currentTarget.style.color = '#dc2626'; e.currentTarget.style.borderColor = '#fca5a5'; } }}
          >
            {submitting ? 'Marking…' : '❌ Not Attended'}
            {!submitting && <span style={{ fontSize: 12, fontWeight: 500, opacity: 0.75 }}>→ Mark as No Show</span>}
          </button>
        </div>
      </div>
    </div>
  </div>
);

/* ─── Appointment Card ──────────────────────────────────────────── */
const DoctorAppointmentCard = ({ appt, onSubmit, onViewResults }) => {
  const dateStr = appt.appointmentDate
    ? new Date(appt.appointmentDate).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })
    : '—';

  const showSubmit      = appt.status === 'CONFIRMED';
  const showViewResults = appt.consultationSubmitted === true || appt.status === 'COMPLETED' || appt.status === 'NO_SHOW';

  return (
    <div style={{
      background: '#fff', border: '1px solid #f3f4f6', borderRadius: 16,
      padding: '18px 20px', transition: 'box-shadow .15s',
    }}
      onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 20px rgba(79,70,229,0.08)'}
      onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
    >
      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
        <div style={{
          width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
          background: 'linear-gradient(135deg, #fef3c7, #fffbeb)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20,
        }}>
          {appt.photoUrl
            ? <img src={appt.photoUrl} alt="" style={{ width: 44, height: 44, borderRadius: '50%', objectFit: 'cover' }} />
            : '🧑‍⚕️'}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700, fontSize: 14.5, color: '#1e1b4b', marginBottom: 2 }}>
            {appt.patientName || 'Patient'}
          </div>
          <div style={{ fontSize: 12, color: '#6b7280', fontFamily: 'DM Sans, sans-serif', marginBottom: 4 }}>
            {appt.age ? `${appt.age} yrs` : ''}{appt.gender ? ` · ${appt.gender.charAt(0) + appt.gender.slice(1).toLowerCase()}` : ''}
            {appt.patientPhone ? ` · 📞 ${appt.patientPhone}` : ''}
          </div>
          {appt.symptoms && (
            <div style={{ fontSize: 12, color: '#4f46e5', fontFamily: 'DM Sans, sans-serif', marginBottom: 6, fontStyle: 'italic' }}>
              "{appt.symptoms}"
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: 11.5, color: '#6b7280', fontFamily: 'DM Sans, sans-serif' }}>📅 {dateStr}</span>
            {appt.slotTime && <span style={{ fontSize: 11.5, color: '#6b7280', fontFamily: 'DM Sans, sans-serif' }}>🕐 {appt.slotTime}</span>}
            {appt.consultationType && (
              <span style={{
                fontSize: 11, fontWeight: 600, padding: '2px 7px', borderRadius: 9999,
                background: '#ecfeff', color: '#0891b2', border: '1px solid #a5f3fc',
                fontFamily: 'DM Sans, sans-serif',
              }}>
                {appt.consultationType === 'online' ? '📱 Online' : '🏥 In-person'}
              </span>
            )}
            {appt.isSecondOpinion && (
              <span style={{
                fontSize: 11, fontWeight: 600, padding: '2px 7px', borderRadius: 9999,
                background: '#fdf4ff', color: '#9333ea', border: '1px solid #e9d5ff',
                fontFamily: 'DM Sans, sans-serif',
              }}>🔍 2nd Opinion</span>
            )}
          </div>
          {(showSubmit || showViewResults) && (
            <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
              {showSubmit && (
                <button
                  onClick={() => onSubmit(appt)}
                  style={{
                    fontSize: 12, fontWeight: 700, padding: '6px 14px', borderRadius: 9999,
                    background: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
                    color: '#fff', border: 'none', cursor: 'pointer',
                    fontFamily: 'DM Sans, sans-serif', transition: 'opacity .12s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.opacity = '0.88'}
                  onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                >
                  📝 Submit Consultation
                </button>
              )}
              {showViewResults && (
                <button
                  onClick={() => onViewResults(appt)}
                  style={{
                    fontSize: 12, fontWeight: 700, padding: '6px 14px', borderRadius: 9999,
                    background: '#eef2ff', color: '#4f46e5', border: '1px solid #c7d2fe',
                    cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', transition: 'all .12s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#4f46e5'; e.currentTarget.style.color = '#fff'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = '#eef2ff'; e.currentTarget.style.color = '#4f46e5'; }}
                >
                  🔍 View Results
                </button>
              )}
            </div>
          )}
        </div>
        <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
          <StatusBadge status={appt.status} />
          {appt.fee != null && (
            <span style={{ fontSize: 11.5, color: '#059669', fontWeight: 700, fontFamily: 'DM Sans, sans-serif' }}>
              ₹{appt.fee}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

/* ─── My Content tab ────────────────────────────────────────────── */
const ContentSkeletonCard = () => (
  <div style={{ background: '#fff', borderRadius: 14, padding: '16px', border: '1px solid #f3f4f6', display: 'flex', gap: 14 }}>
    <div className="skeleton" style={{ width: 90, height: 68, borderRadius: 10, flexShrink: 0 }} />
    <div style={{ flex: 1 }}>
      <div className="skeleton skeleton-line" style={{ width: '22%', height: 16, borderRadius: 9999, marginBottom: 8 }} />
      <div className="skeleton skeleton-line" style={{ width: '80%', height: 13, marginBottom: 6 }} />
      <div className="skeleton skeleton-line" style={{ width: '60%', height: 11, marginBottom: 8 }} />
      <div style={{ display: 'flex', gap: 5 }}>
        {[45, 52, 48].map(w => (
          <div key={w} className="skeleton" style={{ width: w, height: 15, borderRadius: 9999 }} />
        ))}
      </div>
    </div>
  </div>
);

const ContentCard = ({ item, onClick }) => {
  const meta = TYPE_META[item.type] || TYPE_META.ARTICLE;

  const dateStr = item.publishedDate
    ? new Date(item.publishedDate).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
    : '';

  const viewsStr = item.viewCount >= 1000
    ? `${(item.viewCount / 1000).toFixed(1)}k`
    : String(item.viewCount ?? 0);

  return (
    <div className="pub-card" onClick={onClick}>
      <div className="pub-card__thumb">
        {item.thumbnailUrl && <img src={item.thumbnailUrl} alt="" />}
        {item.type === 'VIDEO' && (
          <div className="pub-card__thumb-overlay">
            <div style={{
              width: 24, height: 24, borderRadius: '50%', background: 'rgba(255,255,255,0.92)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', paddingLeft: 2,
            }}>
              <svg width="9" height="10" viewBox="0 0 9 10" fill="#1e1b4b"><path d="M1 1l7 4-7 4V1z"/></svg>
            </div>
          </div>
        )}
      </div>

      <div className="pub-card__body">
        <div className="pub-card__meta">
          <span style={{
            fontSize: 10.5, fontWeight: 700, padding: '2px 7px', borderRadius: 9999,
            background: meta.bg, color: meta.color, border: `1px solid ${meta.border}`,
            fontFamily: 'DM Sans, sans-serif',
          }}>
            {meta.icon} {meta.label}
          </span>
          {item.type === 'VIDEO' && item.videoDurationSecs && (
            <span className="t-meta">⏱ {formatDuration(item.videoDurationSecs)}</span>
          )}
          {item.type !== 'VIDEO' && item.readTimeMinutes && (
            <span className="t-meta">⏱ {item.readTimeMinutes} min read</span>
          )}
        </div>

        <div className="pub-card__title">{item.title}</div>

        <div className="pub-card__footer">
          {item.tags?.slice(0, 3).map(tag => (
            <span key={tag} className="tag-chip">{tag}</span>
          ))}
          <span className="t-meta" style={{ marginLeft: 'auto' }}>
            {dateStr}{viewsStr ? ` · 👁 ${viewsStr}` : ''}
          </span>
        </div>
      </div>
    </div>
  );
};

const MyContentTab = () => {
  const navigate      = useNavigate();
  const { token }      = useAuth();
  const { tenant } = useTenant();
  const [articles, setArticles]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [filter, setFilter]       = useState('ALL');
  const [search, setSearch]       = useState('');

  useEffect(() => {
    if (!token || !tenant) return;
    setLoading(true);
    getMyArticles(token, tenant)
    .then(data => {
      setArticles(data);
      setLoading(false);
    })
    .catch(() => {
      setArticles([]);
      setLoading(false);
    });
  }, [token, tenant]);

  const counts = {
    ALL:        articles.length,
    ARTICLE:    articles.filter(a => a.type === 'ARTICLE').length,
    VIDEO:      articles.filter(a => a.type === 'VIDEO').length,
    WHITEPAPER: articles.filter(a => a.type === 'WHITEPAPER').length,
  };

  const filtered = articles
    .filter(a => filter === 'ALL' || a.type === filter)
    .filter(a => {
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      return (
        a.title.toLowerCase().includes(q) ||
        (a.summary || '').toLowerCase().includes(q) ||
        (a.tags || []).some(t => t.toLowerCase().includes(q))
      );
    });

  return (
    <div>
      {/* Stats strip */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 18, flexWrap: 'wrap' }}>
        {[
          { icon: '📄', label: 'Articles',     value: counts.ARTICLE,    color: '#4f46e5' },
          { icon: '🎬', label: 'Videos',        value: counts.VIDEO,      color: '#dc2626' },
          { icon: '📋', label: 'White Papers',  value: counts.WHITEPAPER, color: '#0891b2' },
        ].map(s => (
          <div key={s.label} style={{
            flex: '1 1 100px', background: '#fff', border: '1px solid #f3f4f6',
            borderRadius: 12, padding: '12px 16px',
            borderLeft: `3px solid ${s.color}`,
          }}>
            <div style={{ fontSize: 18, marginBottom: 4 }}>{s.icon}</div>
            <div style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700, fontSize: 20, color: s.color, marginBottom: 2 }}>
              {loading ? '—' : s.value}
            </div>
            <div style={{ fontSize: 11.5, color: '#9ca3af', fontFamily: 'DM Sans, sans-serif' }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filter + search bar */}
      <div className="card" style={{ marginBottom: 14, padding: '12px 16px' }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Type filter */}
          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
            {CONTENT_FILTERS.filter(f => f.value === 'ALL' || counts[f.value] > 0).map(f => {
              const active = filter === f.value;
              return (
                <button
                  key={f.value}
                  className={`pill-filter${active ? ' pill-filter--active' : ''}`}
                  onClick={() => setFilter(f.value)}
                >
                  {f.label}
                  {counts[f.value] > 0 && (
                    <span className="pill-filter__count">{counts[f.value]}</span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Search */}
          <div className="search-wrap" style={{ flex: '1 1 180px', minWidth: 160, marginBottom: 0 }}>
            <svg className="search-icon" width="15" height="15" fill="none" stroke="#9ca3af" strokeWidth="2" viewBox="0 0 24 24">
              <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35" strokeLinecap="round"/>
            </svg>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="search-input"
              placeholder="Search title, tags…"
              style={{ height: 34, fontSize: 12.5 }}
            />
            {search && (
              <button className="search-clear" onClick={() => setSearch('')} style={{ fontSize: 16 }}>×</button>
            )}
          </div>

          {/* New Content button */}
          <button
            className="btn-primary btn-auto"
            onClick={() => navigate('/doctor/create-content')}
            style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0, fontSize: 12.5, padding: '7px 14px' }}
          >
            ✍️ New Content
          </button>
        </div>
      </div>

      {/* List */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {loading
          ? Array.from({ length: 4 }).map((_, i) => <ContentSkeletonCard key={i} />)
          : filtered.length === 0
            ? (
              <div className="empty-state">
                <div className="empty-icon">📚</div>
                <p className="empty-title">No content yet</p>
                <p className="empty-body">
                  {search
                    ? `No results for "${search}".`
                    : 'Your published articles, videos, and white papers will appear here.'}
                </p>
                {search ? (
                  <button className="btn-primary" style={{ maxWidth: 160, margin: '0 auto' }} onClick={() => setSearch('')}>
                    Clear search
                  </button>
                ) : (
                  <button
                    className="btn-primary"
                    style={{ maxWidth: 200, margin: '16px auto 0' }}
                    onClick={() => navigate('/doctor/create-content')}
                  >
                    ✍️ Create Content
                  </button>
                )}
              </div>
            )
            : filtered.map((item, i) => (
              <div key={item.id} style={{ animation: `fadeUp .3s ${i * 30}ms ease both` }}>
                <ContentCard item={item} onClick={() => navigate(`/articles/${item.id}`)} />
              </div>
            ))
        }
      </div>
    </div>
  );
};

/* ─── Main Doctor Dashboard ──────────────────────────────────────── */
const TABS = [
  { id: 'appointments', label: '📅 Appointments' },
  { id: 'content',      label: '📚 My Content'   },
];

const DoctorDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [activeTab, setActiveTab]               = useState('appointments');
  const [appointments, setAppointments]         = useState([]);
  const [loading, setLoading]                   = useState(true);
  const [error, setError]                       = useState(null);
  const [dateFilter, setDateFilter]             = useState('');
  const [search, setSearch]                     = useState('');
  const [attendanceTarget, setAttendanceTarget] = useState(null);
  const [markingNoShow, setMarkingNoShow]       = useState(false);
  const [noShowError, setNoShowError]           = useState(null);

  const load = useCallback(async (date) => {
    setLoading(true);
    setError(null);
    try {
      const data = await getDoctorAppointments(date || undefined);
      setAppointments(data);
    } catch (e) {
      setError(`Could not load appointments (${e.message})`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDateFilter = (val) => { setDateFilter(val); load(val || undefined); };
  const handleClearDate  = () => { setDateFilter(''); load(undefined); };

  const handleSubmitClick = (appt) => { setNoShowError(null); setAttendanceTarget(appt); };
  const handleAttended    = () => {
    if (!attendanceTarget) return;
    const appt = attendanceTarget;
    setAttendanceTarget(null);
    navigate(`/doctor/consultation/${appt.id}`);
  };
  const handleNotAttended = async () => {
    if (!attendanceTarget) return;
    setMarkingNoShow(true); setNoShowError(null);
    try {
      await submitConsultation(attendanceTarget.id, { patientAttended: false, existingConditions: null, analysis: null, result: null });
      setAppointments(prev => prev.map(a =>
        a.id === attendanceTarget.id ? { ...a, status: 'NO_SHOW', consultationSubmitted: true } : a
      ));
      setAttendanceTarget(null);
    } catch (e) {
      setNoShowError(e?.response?.data?.message || 'Could not mark as No Show. Try again.');
    } finally {
      setMarkingNoShow(false);
    }
  };
  const handleViewResults = (appt) => navigate(`/doctor/consultation/${appt.id}/results`);

  const total     = appointments.length;
  const confirmed = appointments.filter(a => a.status === 'CONFIRMED').length;
  const completed = appointments.filter(a => a.status === 'COMPLETED').length;
  const noShow    = appointments.filter(a => a.status === 'NO_SHOW').length;

  const filtered = appointments.filter(a => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (a.patientName || '').toLowerCase().includes(q) ||
      (a.phone       || '').toLowerCase().includes(q) ||
      (a.symptoms    || '').toLowerCase().includes(q)
    );
  });

  return (
    <div className="page">
      <main className="page-main">

        {/* Header */}
        <div className="fade-up" style={{ marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
            <div>
              <h1 className="t-h1" style={{ marginBottom: 4 }}>🩺 Doctor Dashboard</h1>
              <p className="t-body-sm">
                {user?.name ? `Welcome, Dr. ${user.name.split(' ')[0]}` : 'Manage your appointments and consultations'}
              </p>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              className="btn-primary btn-auto"
              onClick={() => navigate('/doctor/create-content')}
              style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'linear-gradient(135deg, #059669, #0891b2)' }}
            >
              ✍️ Create Content
            </button>
            <button
              className="btn-primary btn-auto"
              onClick={() => navigate('/patients')}
              style={{ display: 'flex', alignItems: 'center', gap: 6 }}
            >
              👥 My Patients
            </button>
            </div>
          </div>
        </div>

        {/* Stats strip — only on appointments tab */}
        {activeTab === 'appointments' && (
          <div className="stat-grid fade-up-1" style={{ marginBottom: '1.5rem' }}>
            {[
              { icon: '📅', label: 'Total Today',  value: total,     color: '#4f46e5' },
              { icon: '✅', label: 'Confirmed',     value: confirmed, color: '#059669' },
              { icon: '🎯', label: 'Completed',     value: completed, color: '#6366f1' },
              { icon: '⚠️', label: 'No Show',       value: noShow,    color: '#d97706' },
            ].map(s => (
              <div key={s.label} className="stat-card" style={{ borderLeft: `3px solid ${s.color}` }}>
                <div style={{ fontSize: 22, marginBottom: 8 }}>{s.icon}</div>
                <div className="stat-value" style={{ color: s.color }}>{loading ? '—' : s.value}</div>
                <div className="stat-label">{s.label}</div>
              </div>
            ))}
          </div>
        )}

        {/* Tab bar */}
        <div className="fade-up-1" style={{
          display: 'flex', gap: 4, marginBottom: '1.25rem',
          borderBottom: '2px solid #f3f4f6', paddingBottom: 0,
        }}>
          {TABS.map(tab => {
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  padding: '10px 18px', border: 'none', background: 'none',
                  fontFamily: 'DM Sans, sans-serif', fontWeight: active ? 700 : 500,
                  fontSize: 13.5, color: active ? '#4f46e5' : '#6b7280',
                  cursor: 'pointer', borderBottom: active ? '2.5px solid #4f46e5' : '2.5px solid transparent',
                  marginBottom: -2, transition: 'color .13s',
                }}
                onMouseEnter={e => { if (!active) e.currentTarget.style.color = '#1e1b4b'; }}
                onMouseLeave={e => { if (!active) e.currentTarget.style.color = '#6b7280'; }}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* ── Appointments tab ──────────────────────────────────── */}
        {activeTab === 'appointments' && (
          <>
            {/* Filters */}
            <div className="card fade-up-2" style={{ marginBottom: '1.25rem', padding: '14px 18px' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <label style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', fontFamily: 'DM Sans, sans-serif', whiteSpace: 'nowrap' }}>
                    📅 Date:
                  </label>
                  <input
                    type="date"
                    value={dateFilter}
                    onChange={e => handleDateFilter(e.target.value)}
                    style={{
                      height: 36, borderRadius: 9, border: '1.5px solid #e5e7eb',
                      padding: '0 10px', fontSize: 13, fontFamily: 'DM Sans, sans-serif',
                      color: '#1e1b4b', outline: 'none', cursor: 'pointer',
                    }}
                  />
                  {dateFilter && (
                    <button
                      onClick={handleClearDate}
                      style={{
                        height: 36, padding: '0 12px', borderRadius: 9,
                        border: '1.5px solid #e5e7eb', background: '#fff',
                        fontSize: 12, fontWeight: 600, color: '#6b7280',
                        cursor: 'pointer', fontFamily: 'DM Sans, sans-serif',
                        display: 'flex', alignItems: 'center', gap: 4,
                      }}
                    >
                      ✕ Clear
                    </button>
                  )}
                </div>
                <div className="search-wrap" style={{ flex: '1 1 200px', minWidth: 180, marginBottom: 0 }}>
                  <svg className="search-icon" width="16" height="16" fill="none" stroke="#9ca3af" strokeWidth="2" viewBox="0 0 24 24">
                    <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35" strokeLinecap="round"/>
                  </svg>
                  <input
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    className="search-input"
                    placeholder="Search by patient name, phone…"
                    style={{ height: 36, fontSize: 13 }}
                  />
                  {search && (
                    <button className="search-clear" onClick={() => setSearch('')} style={{ fontSize: 16 }}>×</button>
                  )}
                </div>
              </div>
            </div>

            {error && (
              <div className="error-state" style={{ marginBottom: '1rem' }}>
                <span>⚠️</span> {error}
                <button className="t-link" style={{ marginLeft: 12 }} onClick={() => load(dateFilter || undefined)}>Retry</button>
              </div>
            )}
            {noShowError && (
              <div className="error-state" style={{ marginBottom: '1rem' }}>
                <span>⚠️</span> {noShowError}
                <button className="t-link" style={{ marginLeft: 10 }} onClick={() => setNoShowError(null)}>Dismiss</button>
              </div>
            )}

            <div className="fade-up-3" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {loading
                ? Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={i} />)
                : filtered.length === 0
                  ? (
                    <div className="empty-state">
                      <div className="empty-icon">🩺</div>
                      <p className="empty-title">No appointments found</p>
                      <p className="empty-body">
                        {dateFilter
                          ? `No appointments on ${new Date(dateFilter + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}.`
                          : 'You have no appointments yet.'}
                      </p>
                      {dateFilter && (
                        <button className="btn-primary" style={{ maxWidth: 180, margin: '0 auto' }} onClick={handleClearDate}>
                          View all appointments
                        </button>
                      )}
                    </div>
                  )
                  : filtered.map((appt, i) => (
                    <div key={appt.id || i} style={{ animation: `fadeUp .3s ${i * 30}ms ease both` }}>
                      <DoctorAppointmentCard
                        appt={appt}
                        onSubmit={handleSubmitClick}
                        onViewResults={handleViewResults}
                      />
                    </div>
                  ))
              }
            </div>

            {/* Recent activity — below the appointments list */}
            <div className="fade-up-3" style={{ marginTop: '1.25rem' }}>
              <RecentActivityCard role={user?.role} />
            </div>
          </>
        )}

        {/* ── My Content tab ───────────────────────────────────── */}
        {activeTab === 'content' && (
          <div className="fade-up-2">
            <MyContentTab />
          </div>
        )}

      </main>

      {attendanceTarget && (
        <AttendancePopup
          appt={attendanceTarget}
          onClose={() => setAttendanceTarget(null)}
          onAttended={handleAttended}
          onNotAttended={handleNotAttended}
          submitting={markingNoShow}
        />
      )}
    </div>
  );
};

export default DoctorDashboard;